import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import api from '@/api/client';
import { fetchChantiers } from '@/store/thunks/chantierThunks';
import { deleteEnginAffectation } from '@/store/thunks/enginAffectationThunks';
import type { Chantier, Engin, EnginAffectation } from '@/types';
import { checkPermission } from '@/utils/checkPermission';
import { Card, ConfirmModal, Modal, SearchSelect } from '@/components/ui';
import { Link2, Unlink2 } from 'lucide-react';
import EnginAffectationForm from './EnginAffectationForm';

type EnginGroup = { famille: string; engins: Engin[] };

function groupEnginByFamille(data: Engin[]): EnginGroup[] {
  const out: EnginGroup[] = [];
  for (const e of data) {
    const famille = e?.famille?.nom ?? 'Sans famille';
    const idx = out.findIndex((g) => g.famille === famille);
    if (idx >= 0) out[idx].engins.push(e);
    else out.push({ famille, engins: [e] });
  }
  return out;
}

function isChantierSelected(c: Chantier | null): c is Chantier {
  return Boolean(c && typeof c.id === 'number');
}

export interface EnginAffectationsSectionProps {
  /** Title above the section. Default: "Affectations engins" */
  title?: string;
  /** Called when modal closes (e.g. to refetch parent list). Optional. */
  onModalClose?: () => void;
}

/**
 * Chantier ↔ Engins affectations.
 * Matches the legacy UI: select chantier, show two grouped lists (affectés / non affectés),
 * click to affect, click to désaffecter with confirmation.
 */
export default function EnginAffectationsSection({
  title = 'Affectations engins',
  onModalClose,
}: EnginAffectationsSectionProps) {
  const dispatch = useAppDispatch();
  const chantiers = useAppSelector((s: RootState) => s.chantier.chantiers);
  const { isAdmin, authorities } = useAppSelector((s: RootState) => s.auth);
  const canAffect = isAdmin || checkPermission('AFFECT_ENGINE', authorities);
  const canDecommission = isAdmin || checkPermission('DECOMMISSION_ENGINE', authorities);

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedChantierId, setSelectedChantierId] = useState<string>('');
  const [selectedChantier, setSelectedChantier] = useState<Chantier | null>(null);
  const [selectedEngin, setSelectedEngin] = useState<Engin | null>(null);
  const [toDelete, setToDelete] = useState<EnginAffectation | null>(null);
  const [filterQuery] = useState('');
  const [affected, setAffected] = useState<EnginGroup[]>([]);
  const [notAffected, setNotAffected] = useState<EnginGroup[]>([]);

  useEffect(() => {
    dispatch(fetchChantiers());
  }, [dispatch]);

  const refreshNotAffected = async () => {
    const { data } = await api.get<{ data: Engin[] }>('/engins/non_affecter');
    const list = Array.isArray(data?.data) ? data.data : [];
    setNotAffected(groupEnginByFamille(list));
  };

  const refreshAffected = async (chantierId: number) => {
    const { data } = await api.get<{ data: Engin[] }>(`/engins/chantier/${chantierId}`);
    const list = Array.isArray(data?.data) ? data.data : [];
    setAffected(groupEnginByFamille(list));
  };

  useEffect(() => {
    void refreshNotAffected();
  }, []);

  useEffect(() => {
    const chantier = chantiers.find((c) => String(c.id) === selectedChantierId) ?? null;
    setSelectedChantier(chantier);
    if (chantier?.id != null) {
      void refreshAffected(chantier.id);
    } else {
      setAffected([]);
    }
  }, [selectedChantierId, chantiers]);

  const handleCloseModal = async () => {
    setModalOpen(false);
    setSelectedEngin(null);
    if (selectedChantier?.id != null) await refreshAffected(selectedChantier.id);
    await refreshNotAffected();
    onModalClose?.();
  };

  const handleSuccess = async () => {
    setModalOpen(false);
    setSelectedEngin(null);
    if (selectedChantier?.id != null) await refreshAffected(selectedChantier.id);
    await refreshNotAffected();
    onModalClose?.();
  };

  const handleDeleteConfirm = () => {
    if (toDelete) {
      dispatch(deleteEnginAffectation(toDelete));
      setToDelete(null);
      setConfirmOpen(false);
    }
  };

  const chantierOptions = useMemo(
    () => chantiers.map((c) => ({ value: String(c.id), label: c.code ?? c.nom ?? String(c.id) })),
    [chantiers]
  );

  const q = filterQuery.trim().toLowerCase();
  const filterGroups = (groups: EnginGroup[]) => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        engins: g.engins.filter((e) => {
          const code = String(e.code ?? '').toLowerCase();
          const type = String(e.type ?? '').toLowerCase();
          const fam = String(e.famille?.nom ?? '').toLowerCase();
          return code.includes(q) || type.includes(q) || fam.includes(q);
        }),
      }))
      .filter((g) => g.engins.length > 0);
  };

  const affectedFiltered = useMemo(() => filterGroups(affected), [affected, q]);
  const notAffectedFiltered = useMemo(() => filterGroups(notAffected), [notAffected, q]);
  const affectedCount = useMemo(
    () => affectedFiltered.reduce((acc, g) => acc + g.engins.length, 0),
    [affectedFiltered]
  );
  const notAffectedCount = useMemo(
    () => notAffectedFiltered.reduce((acc, g) => acc + g.engins.length, 0),
    [notAffectedFiltered]
  );

  const openCreateForEngin = (engin: Engin) => {
    if (!isChantierSelected(selectedChantier)) return;
    setSelectedEngin(engin);
    setModalOpen(true);
  };

  const askRemoveEngin = (engin: Engin) => {
    if (!isChantierSelected(selectedChantier)) return;
    if (engin?.id == null) return;
    const affectation: EnginAffectation = {
      id: 0,
      chantier: { id: selectedChantier.id, code: selectedChantier.code, nom: selectedChantier.nom },
      engine: { id: engin.id, code: engin.code, type: engin.type },
    };
    setToDelete(affectation);
    setConfirmOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            <p className="mt-0.5 text-sm text-slate-600">
              Sélectionnez un chantier, puis affectez/désaffectez des engins.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              Affectés: {affectedCount}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              Non affectés: {notAffectedCount}
            </span>
            
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="md:col-span-1">
            <SearchSelect
              label="Chantier"
              options={chantierOptions}
              value={selectedChantierId}
              onChange={setSelectedChantierId}
              placeholder="Sélectionner un chantier…"
            />
          </div>
          {/* <div className="md:col-span-2">
            <FilterInput
              value={filterQuery}
              onChange={setFilterQuery}
              placeholder="Rechercher (famille, code engin, type)…"
              id="engin-affect-filter"
            />
          </div> */}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          title={
            selectedChantier?.nom
              ? `Engins affectés — ${selectedChantier.nom}`
              : 'Engins affectés'
          }
          className="min-h-[420px]"
        >
          {!isChantierSelected(selectedChantier) ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Sélectionnez un chantier pour afficher les engins affectés.
            </div>
          ) : affectedFiltered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Aucun engin affecté à ce chantier.
            </div>
          ) : (
            <div className="max-h-[520px] space-y-2 overflow-auto pr-1">
              {affectedFiltered.map((g) => (
                <details
                  key={g.famille}
                  className="group overflow-hidden rounded-lg border border-slate-200 bg-white"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                    <span className="truncate">
                      {g.famille}{' '}
                      <span className="ml-1 font-normal text-slate-500">({g.engins.length})</span>
                    </span>
                    <span className="text-xs font-semibold text-slate-500 group-open:hidden">
                      Afficher
                    </span>
                    <span className="hidden text-xs font-semibold text-slate-500 group-open:inline">
                      Masquer
                    </span>
                  </summary>
                  <ul className="divide-y divide-slate-100">
                    {g.engins.map((e) => (
                      <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">
                            {e.code ?? `#${e.id}`}
                          </div>
                          <div className="truncate text-xs text-slate-500">
                            {[e.type, e.unite].filter(Boolean).join(' • ') || '—'}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 shadow-sm hover:bg-rose-100 hover:shadow disabled:opacity-50"
                          disabled={!canDecommission}
                          onClick={() => askRemoveEngin(e)}
                        >
                          <Unlink2 className="h-4 w-4" />
                          Désaffecter
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          )}
        </Card>

        <Card title="Engins non affectés" className="min-h-[420px]">
          {notAffectedFiltered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Aucun engin non affecté.
            </div>
          ) : (
            <div className="max-h-[520px] space-y-2 overflow-auto pr-1">
              {notAffectedFiltered.map((g) => (
                <details
                  key={g.famille}
                  className="group overflow-hidden rounded-lg border border-slate-200 bg-white"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                    <span className="truncate">
                      {g.famille}{' '}
                      <span className="ml-1 font-normal text-slate-500">({g.engins.length})</span>
                    </span>
                    <span className="text-xs font-semibold text-slate-500 group-open:hidden">
                      Afficher
                    </span>
                    <span className="hidden text-xs font-semibold text-slate-500 group-open:inline">
                      Masquer
                    </span>
                  </summary>
                  <ul className="divide-y divide-slate-100">
                    {g.engins.map((e) => (
                      <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-slate-900">
                            {e.code ?? `#${e.id}`}
                          </div>
                          <div className="truncate text-xs text-slate-500">
                            {[e.type, e.unite].filter(Boolean).join(' • ') || '—'}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-white uppercase rounded-md border border-teal-400 bg-teal-500 px-3 py-2 text-xs font-semibold text-teal-800 shadow-sm hover:bg-teal-500 hover:shadow disabled:opacity-50"
                          disabled={!canAffect || !isChantierSelected(selectedChantier)}
                          onClick={() => openCreateForEngin(e)}
                          title={!isChantierSelected(selectedChantier) ? 'Sélectionnez un chantier' : undefined}
                        >
                          <Link2 className="h-4 w-4" />
                          Affecter
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Modal open={modalOpen} onClose={handleCloseModal} title="Affectation des engins" size="lg">
        <EnginAffectationForm
          affectation={null}
          type="create"
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
          prefill={
            isChantierSelected(selectedChantier) || selectedEngin?.id != null
              ? {
                  chantierId: selectedChantier?.id,
                  engineId: selectedEngin?.id,
                  lockChantier: isChantierSelected(selectedChantier),
                  lockEngine: Boolean(selectedEngin?.id),
                }
              : undefined
          }
        />
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        message={
          toDelete?.engine?.code && toDelete?.chantier?.code
            ? `Voulez-vous désaffecter engin (${toDelete.engine.code}) de chantier (${toDelete.chantier.code}) ?`
            : 'Voulez-vous désaffecter cet engin ?'
        }
      />
    </div>
  );
}
